import { useMemo, useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertCircle, Building2, CheckCircle2, ChevronDown, ChevronUp, Clock, ExternalLink, Eye, FileText, RefreshCw, Send, ShieldCheck,
} from 'lucide-react';
import { contactsIn, filterDocuments, isPending, summarize } from '../../utils/companyDocuments';
import { applyDocumentFilters, hasActiveFilters, NO_FILTERS } from '../../utils/documentSearch';
import DocumentSearchBar from './DocumentSearchBar';

// Read-only HyreDocs tab for a Bullhorn Company record: every document sent to
// any of the company's contacts. A row can be VIEWED (preview, signed PDF and
// audit certificate) and OPENED in HyreDocs, exactly as on the Contact tab, but
// has no send / remind / cancel / copy-link actions — to act on a document, open
// the contact it was sent to.

const STATUS = {
  sent: { label: 'Sent', icon: Send, bg: 'bg-blue-100', color: 'text-blue-700' },
  opened: { label: 'Opened', icon: Eye, bg: 'bg-amber-100', color: 'text-amber-700' },
  in_progress: { label: 'In progress', icon: Clock, bg: 'bg-amber-100', color: 'text-amber-700' },
  completed: { label: 'Completed', icon: CheckCircle2, bg: 'bg-emerald-100', color: 'text-emerald-700' },
  pending_approval: { label: 'Pending approval', icon: Clock, bg: 'bg-violet-100', color: 'text-violet-700' },
  approved: { label: 'Approved', icon: CheckCircle2, bg: 'bg-emerald-100', color: 'text-emerald-700' },
  expired: { label: 'Expired', icon: AlertCircle, bg: 'bg-red-100', color: 'text-red-700' },
};

// The shared outline Button, written out: this component is also rendered under
// jest, which cannot resolve the "@/" alias the shared Button imports through.
const ACTION_BUTTON = 'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input shadow-sm transition-colors hover:bg-slate-100 h-7 px-3 text-xs font-medium';

const when = (iso, pattern) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : format(d, pattern);
};

export default function CompanyDocumentsTab({ entityId, entityInfo, documents, onRefresh, apiUrl = '', onPreview, onOpenInHyreDocs }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [contactFilter, setContactFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState(NO_FILTERS);

  const stats = useMemo(() => summarize(documents), [documents]);
  const contacts = useMemo(() => contactsIn(documents), [documents]);
  const visible = useMemo(
    () => applyDocumentFilters(filterDocuments(documents, statusFilter, contactFilter), search),
    [documents, statusFilter, contactFilter, search],
  );
  const contactCount = entityInfo?.contacts?.length;

  const chips = [
    { v: 'all', label: `All (${stats.total})` },
    { v: 'pending', label: `Pending (${filterDocuments(documents, 'pending').length})` },
    { v: 'opened', label: `Opened (${stats.opened})` },
    { v: 'completed', label: `Completed (${stats.completed})` },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="company-tab">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#1a4a5e]/10 flex-shrink-0">
                <Building2 className="w-4 h-4 text-[#1a4a5e]" />
              </div>
              <div className="min-w-0">
                <h1 className="font-semibold text-sm truncate" data-testid="company-tab-name">
                  {entityInfo?.name || `Company #${entityId}`}
                </h1>
                {contactCount != null && (
                  <p className="text-xs text-muted-foreground">
                    {contactCount} {contactCount === 1 ? 'contact' : 'contacts'}
                  </p>
                )}
              </div>
            </div>
            {stats.pending > 0 && (
              <span className="bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full" data-testid="company-tab-pending-badge">
                {stats.pending} Pending
              </span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-3 text-xs" data-testid="company-tab-stats">
            <span className="font-medium">{stats.total} Total</span>
            <span className="flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" /> {stats.completed} Complete
            </span>
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="w-3 h-3" /> {stats.pending} Pending
            </span>
            <button
              type="button"
              onClick={onRefresh}
              className="ml-auto h-6 px-2 rounded hover:bg-slate-100"
              title="Refresh"
              aria-label="Refresh documents"
              data-testid="company-tab-refresh"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          </div>

          {documents.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="company-status-chips">
              {chips.map((chip) => (
                <button
                  key={chip.v}
                  type="button"
                  onClick={() => setStatusFilter(chip.v)}
                  aria-pressed={statusFilter === chip.v}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    statusFilter === chip.v ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`company-status-chip-${chip.v}`}
                >
                  {chip.label}
                </button>
              ))}
              {contacts.length > 1 && (
                <select
                  value={contactFilter}
                  onChange={(e) => setContactFilter(e.target.value)}
                  className="ml-auto max-w-[180px] text-[11px] border rounded-full px-2 py-1 bg-white text-slate-700"
                  aria-label="Filter by contact"
                  data-testid="company-contact-filter"
                >
                  <option value="all">All contacts ({contacts.length})</option>
                  {contacts.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {documents.length > 0 && (
            <DocumentSearchBar filters={search} onChange={setSearch} shown={visible.length} total={documents.length} testId="company-search" />
          )}
        </div>
      </div>

      <div className="p-4 space-y-2">
        {documents.length === 0 ? (
          <div className="text-center py-12" data-testid="company-tab-empty">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No documents have been sent to this company&apos;s contacts yet</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10" data-testid="company-tab-filtered-empty">
            <p className="text-sm text-slate-500">
              {hasActiveFilters(search) ? 'No documents match your search.' : 'No documents match this filter.'}
            </p>
            <button
              type="button"
              className="mt-2 text-xs text-slate-600 underline"
              onClick={() => { setStatusFilter('all'); setContactFilter('all'); setSearch(NO_FILTERS); }}
            >
              Show all
            </button>
          </div>
        ) : (
          visible.map((doc) => {
            const status = STATUS[doc.status] || STATUS.sent;
            const StatusIcon = status.icon;
            const open = expanded === doc.id;
            return (
              <div key={doc.id} className="bg-white rounded-lg border shadow-sm overflow-hidden" data-testid={`company-doc-${doc.id}`}>
                <button
                  type="button"
                  className="w-full p-3 flex items-center gap-3 text-left hover:bg-slate-50"
                  onClick={() => setExpanded(open ? null : doc.id)}
                  aria-expanded={open}
                >
                  <div className={`w-8 h-8 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-4 h-4 ${status.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-sm truncate">{doc.template_name}</p>
                      {doc.packet_name && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 truncate max-w-[160px]" title={`Packet: ${doc.packet_name}`}>
                          {doc.packet_name}
                        </span>
                      )}
                      {doc.placement_id && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700" title="Sent from this placement" data-testid={`company-doc-placement-${doc.id}`}>
                          Placement #{doc.placement_id}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      <span className="font-medium text-slate-700" data-testid={`company-doc-contact-${doc.id}`}>{doc.candidate_name || doc.candidate_email}</span>
                      {' · '}
                      {doc.sent_at ? formatDistanceToNow(new Date(doc.sent_at), { addSuffix: true }) : 'Unknown'}
                    </p>
                  </div>
                  <span className={`${status.bg} ${status.color} text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0`}>
                    {status.label}
                  </span>
                  {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {open && (
                  <div className="px-3 pb-3 border-t bg-slate-50">
                    <div className="grid grid-cols-2 gap-2 py-2 text-xs">
                      <div className="col-span-2 min-w-0">
                        <span className="text-muted-foreground">Contact:</span>
                        <span className="ml-1 font-medium">{doc.candidate_name}</span>
                        {doc.candidate_email && <span className="ml-1 text-muted-foreground break-all">({doc.candidate_email})</span>}
                      </div>
                      {doc.placement_id && (
                        <div className="col-span-2"><span className="text-muted-foreground">Placement:</span><span className="ml-1 font-medium">#{doc.placement_id}</span></div>
                      )}
                      <div><span className="text-muted-foreground">Sent:</span><span className="ml-1 font-medium">{when(doc.sent_at, 'MMM d, yyyy h:mm a')}</span></div>
                      {doc.opened_at && <div><span className="text-muted-foreground">Opened:</span><span className="ml-1 font-medium">{when(doc.opened_at, 'MMM d, yyyy h:mm a')}</span></div>}
                      {doc.completed_at && <div><span className="text-muted-foreground">Completed:</span><span className="ml-1 font-medium">{when(doc.completed_at, 'MMM d, yyyy h:mm a')}</span></div>}
                      {doc.approved_at && <div><span className="text-muted-foreground">Approved:</span><span className="ml-1 font-medium">{when(doc.approved_at, 'MMM d, yyyy h:mm a')}</span></div>}
                      {doc.expires_at && isPending(doc) && <div><span className="text-muted-foreground">Expires:</span><span className="ml-1 font-medium">{when(doc.expires_at, 'MMM d, yyyy')}</span></div>}
                      {doc.requires_approval && <div className="col-span-2 text-violet-700">Requires admin approval before syncing to Bullhorn</div>}
                      {!isPending(doc) && doc.completed_at && (
                        <div className="col-span-2 flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1" data-testid={`company-signed-${doc.id}`}>
                          <ShieldCheck className="w-3 h-3" />
                          <span>Signed &amp; audited</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 pb-1" data-testid={`company-doc-actions-${doc.id}`}>
                      {isPending(doc) && onPreview && (
                        <button
                          type="button"
                          className={ACTION_BUTTON}
                          onClick={() => onPreview(doc)}
                          data-testid={`company-preview-${doc.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </button>
                      )}
                      {doc.completed_document_url && (
                        <button
                          type="button"
                          className={ACTION_BUTTON}
                          onClick={() => window.open(`${apiUrl}${doc.completed_document_url}`, '_blank')}
                          data-testid={`company-view-signed-${doc.id}`}
                        >
                          <FileText className="w-4 h-4 mr-1" />
                          View signed PDF &amp; audit certificate
                        </button>
                      )}
                      {onOpenInHyreDocs && (
                        <button
                          type="button"
                          className={ACTION_BUTTON}
                          onClick={() => onOpenInHyreDocs(doc)}
                          title="Open the full tracking page in HyreDocs"
                          data-testid={`company-open-hyredocs-${doc.id}`}
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Open in HyreDocs
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
        {entityInfo?.truncated && (
          <p className="text-[11px] text-amber-700 text-center pt-2" data-testid="company-tab-truncated">
            Showing the most recent documents only. Older documents are not listed or searchable here.
          </p>
        )}
        <p className="text-[11px] text-slate-400 text-center pt-2" data-testid="company-tab-note">
          Documents sent to this company&apos;s contacts. To send or manage a document, open the contact.
        </p>
      </div>
    </div>
  );
}
